import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import {
  PaymentStateService,
  BillingInfo,
} from "../../services/payment-state.service";
import { PaymentApiService } from "../../api/payment-api.service";
import { CountryList, stateList } from "../plan/invoice-dialog/states";

@Component({
    selector: "app-billing-step",
    templateUrl: "./billing-step.component.html",
    styleUrls: ["./billing-step.component.scss"],
    standalone: false
})
export class BillingStepComponent implements OnInit {
  // Form groups for different user types
  indianForm!: FormGroup;
  internationalForm!: FormGroup;

  // Data sources
  states = stateList;
  countries = CountryList;
  district: string[] = [];

  // User type flag
  isInternationalPayment = false;

  // Loading states
  isLoading = false;
  isSaving = false;
  showValidationError = false;

  // Filtered options for autocomplete
  filteredCountries = this.countries;
  filteredStates = this.states;
  filteredDistricts: string[] = [];

  // GST logic properties
  private readonly PLACE_OF_SUPPLY = "Karnataka";
  private readonly PLACE_OF_SUPPLY_CODE = "29"; // Karnataka GST code

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private paymentState: PaymentStateService,
    private paymentApi: PaymentApiService
  ) {}

  ngOnInit() {
    this.isInternationalPayment = this.paymentState.isInternationalPayment;
    this.initializeForms();
    this.setupFormSubscriptions();
    this.loadSavedBillingAddress();
  }

  private initializeForms(): void {
    // Indian form with all required fields
    this.indianForm = this.fb.group({
      country: ["", Validators.required],
      state: ["", Validators.required],
      district: ["", Validators.required],
      pinCode: [
        "",
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
      addressLine: ["", Validators.required],
      gymName: [""],
      hasGST: [false],
      gstNumber: [""],
    });

    // International form with minimal required fields
    this.internationalForm = this.fb.group({
      country: ["", Validators.required],
      addressLine: ["", Validators.required],
      gymName: [""],
    });
  }

  private setupFormSubscriptions(): void {
    // Indian form subscriptions
    this.indianForm.get("state")?.valueChanges.subscribe(() => {
      this.loadCities();
    });

    this.indianForm.get("hasGST")?.valueChanges.subscribe((value: boolean) => {
      const gstControl = this.indianForm.get("gstNumber");
      if (value) {
        gstControl?.setValidators([
          Validators.required,
          Validators.pattern(
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/
          ),
        ]);
      } else {
        gstControl?.clearValidators();
        gstControl?.setValue("");
      }
      gstControl?.updateValueAndValidity();
    });
  }

  private loadSavedBillingAddress(): void {
    this.isLoading = true;
    this.paymentApi
      .getBillingAddress(this.paymentState.selectedBranchId)
      .then((res) => {
        if (res?.data) {
          this.populateFormWithSavedData(res.data);
        }
      })
      .catch((error) => {
        console.error("Error loading billing address:", error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private populateFormWithSavedData(data: any): void {
    const isIndia = data.country === "India";

    if (isIndia) {
      // Populate Indian form
      const country = this.countries.find((c) => c.label === data.country);
      const state = this.states.find((s) => s.state === data.state);

      this.indianForm.patchValue({
        country: country || { label: data.country, value: "IN" },
        state: state || { state: data.state, districts: [] },
        district: data.district || "",
        pinCode: data.pinCode || "",
        addressLine: data.addressLine || "",
        gymName: data.gymName || "",
        hasGST: data.hasGST || false,
        gstNumber: data.gstNumber || "",
      });

      // Load districts for the selected state
      this.loadCities();
    } else {
      // Populate International form
      const country = this.countries.find((c) => c.label === data.country);

      this.internationalForm.patchValue({
        country: country || { label: data.country, value: data.country },
        addressLine: data.addressLine || "",
        gymName: data.gymName || "",
      });
    }
  }

  loadCities(): void {
    const stateValue = this.indianForm.get("state")?.value;
    const stateName =
      typeof stateValue === "string" ? stateValue : stateValue?.state;

    if (stateName) {
      this.district =
        this.states.find((s) => s.state === stateName)?.districts || [];
      this.filteredDistricts = this.district;
    } else {
      this.district = [];
      this.filteredDistricts = [];
    }
  }

  // Filter methods for autocomplete
  filterCountries(value: string): void {
    const filterValue = value.toLowerCase();
    this.filteredCountries = this.countries.filter((country) =>
      country.label.toLowerCase().includes(filterValue)
    );
  }

  filterStates(value: string): void {
    const filterValue = value.toLowerCase();
    this.filteredStates = this.states.filter((state) =>
      state.state.toLowerCase().includes(filterValue)
    );
  }

  filterDistricts(value: string): void {
    const filterValue = value.toLowerCase();
    this.filteredDistricts = this.district.filter((district) =>
      district.toLowerCase().includes(filterValue)
    );
  }

  // Display functions for autocomplete
  displayCountry(country: any): string {
    return country ? country.label : "";
  }

  displayState(state: any): string {
    return state ? state.state : "";
  }

  displayDistrict(district: string): string {
    return district || "";
  }

  goBack(): void {
    this.router.navigate(["/plan"]);
  }

  continueToReview(): void {
    const currentForm = this.isInternationalPayment
      ? this.internationalForm
      : this.indianForm;

    if (currentForm.valid) {
      this.isSaving = true;
      const billingInfo = this.prepareBillingInfo(currentForm.value);
      // Save billing info to state service
      this.paymentState.setBillingInfo({
        ...billingInfo,
        subId: this.paymentState.selectedBranchId,
      });

      // Save to backend and navigate
      this.paymentApi
        .saveBillingAddress({
          ...this.paymentState.billingInfo,
        })
        .then(() => this.router.navigate(["/review-pay"]))
        .catch((err) => {
          console.error("Save billing address error:", err);
          this.router.navigate(["/review-pay"]);
        })
        .finally(() => {
          this.isSaving = false;
        });
    } else {
      // Form is invalid, mark all fields as touched to show validation errors
      currentForm.markAllAsTouched();

      // Show error message and scroll to first error
      this.showValidationErrors();
    }
  }

  private showValidationErrors(): void {
    // Show validation error message
    this.showValidationError = true;

    // Hide error message after 8 seconds (longer for better UX)
    setTimeout(() => {
      this.showValidationError = false;
    }, 8000);

    // Scroll to first error field
    setTimeout(() => {
      const firstError = document.querySelector(".mat-form-field.ng-invalid");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  }

  private prepareBillingInfo(formData: any): BillingInfo {
    const billingInfo: BillingInfo = {
      country: this.getCountryLabel(formData.country),
      addressLine: formData.addressLine,
      gymName: formData.gymName || "",
      hasGST: formData.hasGST || false,
      gstNumber: formData.gstNumber || "",
    };

    // Add Indian-specific fields
    if (!this.isInternationalPayment) {
      billingInfo.state = this.getStateName(formData.state);
      billingInfo.district = formData.district || "";
      billingInfo.pinCode = formData.pinCode || "";
    }

    return billingInfo;
  }

  private getCountryLabel(country: any): string {
    if (typeof country === "object" && country?.label) {
      return country.label;
    } else if (typeof country === "string") {
      const selectedCountry = this.countries.find((c) => c.value === country);
      return selectedCountry?.label || country;
    }
    return "";
  }

  private getStateName(state: any): string {
    if (typeof state === "object" && state?.state) {
      return state.state;
    }
    return state || "";
  }
}
